import * as api from "@/api";
import EditShortcutBackButton from "@/components/edit-shortcut-back-button";
import styles from "@/components/edit-shortcut-popup.module.css";
import useCustomInput from "@/hooks/use-custom-input";
import * as signals from "@/signals";
import { EditShortcutPopupPage, IconOption, SiteInfoResponse } from "@/types";
import { getColorUrl, getRandomColorSet, handleKeyboardClick } from "@/utils";
import { faCheck, faDice } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useQuery } from "@tanstack/react-query";
import clsx from "clsx";
import { t } from "i18next";
import { CSSProperties, JSX, MouseEvent, useState } from "react";

interface EditShortcutIconPageProps {
	isShown: boolean;
	selectedIconOption: IconOption;
	setCurrentPage: (newValue: EditShortcutPopupPage) => void;
	setIcon: (iconOption: IconOption, url: string) => void;
}

function EditShortcutIconPage({
	isShown,
	selectedIconOption,
	setCurrentPage,
	setIcon,
}: EditShortcutIconPageProps): JSX.Element {
	const [limeGeneratedIconPreviewStyle, setLimeGeneratedIconPreviewStyle] =
		useState<CSSProperties>(getRandomColorSet);

	const { data, isFetching } = useQuery<SiteInfoResponse | null>({
		queryFn: () => {
			if (!isShown || !signals.editingShortcut.value.url) {
				return null;
			}
			return api.getSiteInfo(signals.editingShortcut.value.url);
		},
		queryKey: ["siteInfo", signals.editingShortcut.value.url, isShown],
	});

	const iconUrlInput = useCustomInput<HTMLInputElement>({
		setValue: (newValue) => {
			signals.editingShortcut.value = {
				...signals.editingShortcut.value,
				icon: newValue,
			};
		},
	});

	const handleRandomButtonClick = (
		event: MouseEvent<HTMLButtonElement>,
	): void => {
		event.stopPropagation();
		const randomColorSet = getRandomColorSet();
		setLimeGeneratedIconPreviewStyle(randomColorSet);
		signals.editingShortcut.value = {
			...signals.editingShortcut.value,
			icon: getColorUrl(randomColorSet),
		};
	};

	const handleUrlInputClick = (event: MouseEvent<HTMLInputElement>): void => {
		event.stopPropagation();
	};

	const options = [
		{
			description: t("autoObtainAndSetMostSuitableIcon"),
			iconStyleClass: styles["icon-preview-auto"],
			iconText: "A",
			isSelected: selectedIconOption === IconOption.AUTO,
			onClick: (): void => {
				setIcon(IconOption.AUTO, "");
			},
			title: t("auto"),
		},
		{
			description: ((): string => {
				if (data?.icon) {
					return t("defaultIconObtained");
				}
				if (isFetching) {
					return t("fetching");
				}
				return t("fullUrlNotFilledIn");
			})(),
			iconStyle: data?.icon
				? { backgroundImage: `url(${data.icon})` }
				: undefined,
			iconStyleClass: styles["icon-preview-image"],
			iconText: data?.icon ? "" : "?",
			isSelected: selectedIconOption === IconOption.WEBSITE_DEFAULT,
			onClick: (): void => {
				setIcon(IconOption.WEBSITE_DEFAULT, data?.icon || "");
			},
			title: t("websiteDefault"),
		},
		{
			extraElement: (
				<button
					onClick={handleRandomButtonClick}
					type="button"
				>
					<FontAwesomeIcon icon={faDice} />
					{t("random")}
				</button>
			),
			iconStyle: limeGeneratedIconPreviewStyle,
			iconStyleClass: styles["icon-preview-generated"],
			iconText: signals.editingShortcut.value.title
				? signals.editingShortcut.value.title.substring(0, 1)
				: "青",
			isSelected: selectedIconOption === IconOption.LIME_GENERATED,
			onClick: (): void => {
				setIcon(
					IconOption.LIME_GENERATED,
					getColorUrl(limeGeneratedIconPreviewStyle),
				);
			},
			title: t("generatedByLime"),
		},
		{
			extraElement: (
				<input
					autoComplete="off"
					className="input"
					inputMode="url"
					onClick={handleUrlInputClick}
					placeholder={t("urlOfIconImage")}
					value={signals.editingShortcut.value.icon}
					{...iconUrlInput}
				/>
			),
			iconStyleClass: styles["icon-preview-image"],
			iconText: "?",
			isSelected: selectedIconOption === IconOption.URL,
			onClick: (): void => {
				setIcon(IconOption.URL, signals.editingShortcut.value.icon);
			},
			title: t("specifiedUrl"),
		},
	];

	const optionElements = options.map((option) => (
		<div
			aria-checked={option.isSelected}
			className={styles["icon-option-container"]}
			key={option.title}
			role="radio"
			tabIndex={0}
			onClick={option.onClick}
			onKeyDown={handleKeyboardClick(option.onClick)}
		>
			<div className={styles["icon-option"]}>
				<div
					className={clsx(
						styles["icon-preview"],
						option.iconStyleClass,
					)}
					style={option.iconStyle}
				>
					{option.iconText}
				</div>
				<div>
					<div>{option.title}</div>
					{option.description && (
						<div className={styles["icon-description"]}>
							{option.description}
						</div>
					)}
					{option.extraElement}
				</div>
			</div>
			{option.isSelected && (
				<FontAwesomeIcon
					icon={faCheck}
					size="lg"
				/>
			)}
		</div>
	));

	return (
		<div
			className={clsx(
				styles["page"],
				!isShown && styles["invisible"],
				isShown && styles["slid"],
			)}
		>
			<div className={styles["title-bar"]}>
				<EditShortcutBackButton setCurrentPage={setCurrentPage} />
				{t("customizeShortcutIcon")}
			</div>
			{optionElements}
		</div>
	);
}

export default EditShortcutIconPage;
